import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Grid, Link, Typography } from "@mui/material";
import { format } from "date-fns";
import { baseTheme } from "../../../themes/shipinsure";
import { getClaimTrackingUrl } from "../getTrackingUrl";
import { requestTerm } from "../status/returnState";
import {
  statusColors,
  statusPanelSx,
  statusSectionTitleSx,
} from "../statusStyles";
import type { Claim, ClaimTranslator } from "../types";

type OrderAndClaimDetailsProps = {
  claim: Claim;
  t: ClaimTranslator;
};

export const OrderAndClaimDetails = ({
  claim,
  t,
}: OrderAndClaimDetailsProps) => {
  const orderDate = claim.order?.ordered_at;
  const description = claim.additional_info || "";
  const trackingUrl = getClaimTrackingUrl(claim);
  const requestType = requestTerm(claim);

  return (
    <Box mb="16px" sx={statusPanelSx()}>
      <Typography sx={{ ...statusSectionTitleSx, mb: "16px" }}>
        {t("status.details", { requestType })}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Detail
            label={t("status.orderNumber")}
            value={`#${claim.order_number}`}
            breakAll
          />
          {trackingUrl ? (
            <Link
              href={trackingUrl}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                mt: "6px",
                fontSize: "14px",
                fontWeight: 600,
                color: baseTheme.colors.primary,
              }}
            >
              {t("status.viewTracking")}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Detail
            label={t("status.orderDate")}
            value={
              orderDate ? format(new Date(orderDate), "MMMM d, yyyy") : "—"
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Detail
            label={t("status.claimDate")}
            value={format(new Date(claim.created_at), "MMMM d, yyyy")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Detail
            label={t("status.reason", { requestType })}
            value={claim.reason}
            capitalize
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Detail
            label={t("status.resolution")}
            value={claim.preferred_resolution}
            capitalize
          />
        </Grid>
      </Grid>

      {description ? (
        <Box
          mt="16px"
          pt="16px"
          sx={{ borderTop: `1px solid ${statusColors.divider}` }}
        >
          <Typography
            fontWeight={700}
            fontSize="14px"
            color={statusColors.metaText}
            mb="6px"
          >
            {t("status.description")}
          </Typography>
          <Typography
            fontSize="15px"
            color={baseTheme.colors.text}
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.65,
            }}
          >
            {description}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

const Detail = ({
  label,
  value,
  capitalize = false,
  breakAll = false,
}: {
  label: string;
  value?: string;
  capitalize?: boolean;
  breakAll?: boolean;
}) => {
  const displayValue =
    value != null && value !== "" ? String(value).replace(/_/g, " ") : "—";

  return (
    <Box>
      <Typography
        fontWeight={700}
        fontSize="14px"
        color={statusColors.metaText}
        mb="2px"
      >
        {label}
      </Typography>
      <Typography
        fontSize="15px"
        color={baseTheme.colors.text}
        textTransform={capitalize ? "capitalize" : "none"}
        sx={breakAll ? { wordBreak: "break-all" } : undefined}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};
